import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  callback: () => void;
  description?: string;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  ignoreWhenTyping?: boolean;
}

/**
 * Custom hook for registering keyboard shortcuts
 *
 * Features:
 * - Register multiple shortcuts with callbacks
 * - Handle modifier keys (Ctrl, Cmd, Shift, Alt)
 * - Prevent conflicts with browser shortcuts
 * - Don't trigger when user is typing in input/textarea
 * - Clean up listeners on unmount
 *
 * @example
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     { key: 'e', callback: () => setIsEditing(true), description: 'Edit mode' },
 *     { key: 's', ctrlKey: true, metaKey: true, callback: handleSave, preventDefault: true },
 *   ]
 * });
 */
export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
  ignoreWhenTyping = true,
}: UseKeyboardShortcutsOptions) {
  const shortcutsRef = useRef(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const isTypingElement = useCallback((element: Element | null): boolean => {
    if (!element) return false;

    const tagName = element.tagName.toLowerCase();
    const isContentEditable = element.getAttribute('contenteditable') === 'true';
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isSelect = tagName === 'select';

    return isInput || isContentEditable || isSelect;
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Check if user is typing in an input field
    if (ignoreWhenTyping && isTypingElement(document.activeElement)) {
      return;
    }

    const key = event.key.toLowerCase();

    // Find matching shortcut
    const matchingShortcut = shortcutsRef.current.find(shortcut => {
      const keyMatches = shortcut.key.toLowerCase() === key;

      // For modifier keys, we need to handle the case where both ctrlKey and metaKey are specified
      // This allows Ctrl+S on Windows/Linux and Cmd+S on Mac with a single shortcut definition
      const hasRequiredModifier =
        (shortcut.ctrlKey && event.ctrlKey) ||
        (shortcut.metaKey && event.metaKey);

      const modifierMatches =
        (shortcut.ctrlKey || shortcut.metaKey) ? hasRequiredModifier : true;

      const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
      const altMatches = shortcut.altKey ? event.altKey : !event.altKey;

      return keyMatches && modifierMatches && shiftMatches && altMatches;
    });

    if (matchingShortcut) {
      if (matchingShortcut.preventDefault) {
        event.preventDefault();
      }
      matchingShortcut.callback();
    }
  }, [enabled, ignoreWhenTyping, isTypingElement]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    isEnabled: enabled,
  };
}
